import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowLeft, MessageSquare, Brain, Heart, Volume2, Bookmark, Shield, HardDrive, Share2, Target, Users, Sparkles, BookOpen, Zap, Palette, Globe, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppLayout } from "@/components/AppLayout";

const GUIDE_SECTIONS = [
  {
    id: "modules", title: "Chat Modules (6 Guides)", icon: MessageSquare,
    what: "Omni has 6 specialist AI guides, each trained for a different area of your life: Friend, Mentor, Social Guide, Job Guardian, Life Coach, and Storyteller.",
    when: "Switch modules based on what you need right now. Having a bad day? The Friend offers emotional support. Need career advice? Job Guardian. Want motivation? Mentor.",
    steps: ["Tap the module selector at the top of the chat", "Browse all 6 guides (plus 3 exclusive modes)", "Select one — your conversation instantly adapts to that guide's expertise", "You can switch mid-conversation without losing context"],
    tips: ["Friend is the default and works great for everyday chat", "Storyteller creates parables and stories with life lessons", "Social Guide helps you rehearse difficult conversations before they happen"],
  },
  {
    id: "templates", title: "Quick-Start Templates", icon: Zap,
    what: "18 curated conversation starters across 6 categories: Daily Check-in, Personal Growth, Career, Emotional Support, Relationships, and Life & Purpose.",
    when: "When you're not sure where to start, or want to jump into a meaningful conversation instantly.",
    steps: ["On the welcome screen, browse the category tabs", "Tap a category (e.g. Career & Work)", "Click any template card to populate the input", "The correct module auto-selects — just hit send"],
    tips: ["Templates are a starting point — you can edit the text before sending", "Each template routes to the best module for that topic"],
  },
  {
    id: "wisdom", title: "Wisdom Council", icon: Sparkles,
    what: "22 historical mentors — from Marcus Aurelius to Maya Angelou — each with a unique AI voice. They bring the wisdom of history into your personal conversations.",
    when: "When you want perspective from a specific thinker, philosopher, or leader. Great for deep life questions.",
    steps: ["Tap the 'Wisdom' tab next to the module selector", "Browse the 22 figures organized by era", "Select a figure — they speak in their voice and style", "Their unique TTS voice plays automatically on responses"],
    tips: ["Try Marcus Aurelius for stoic discipline, Rumi for spiritual insight, Maya Angelou for courage", "Wisdom Council figures auto-speak — no need to enable TTS separately"],
  },
  {
    id: "response-mode", title: "Concise vs Comprehensive", icon: Layers,
    what: "A toggle that controls how detailed Omni's responses are. Concise gives you sharp 2-4 sentence answers. Comprehensive provides deep, structured exploration.",
    when: "Concise for quick check-ins and everyday chat. Comprehensive when you want Omni to really dig into a topic.",
    steps: ["Look at the pill toggle above the text input", "Tap 'Concise' for short, sharp responses", "Tap 'Comprehensive' for detailed, in-depth responses", "Your choice persists across sessions"],
    tips: ["Concise mode never parrots back your question — it leads with the answer", "Comprehensive mode structures longer answers with clear sections"],
  },
  {
    id: "memory", title: "Omni's Memory", icon: Brain,
    what: "Omni learns and remembers facts about you — your preferences, life events, goals, relationships, and more. These memories make every conversation more personal.",
    when: "Check your memory panel periodically to see what Omni has learned. Edit or delete anything you want.",
    steps: ["Tap the Brain icon in the chat header", "Browse memories grouped by category", "Edit any memory by clicking the pencil icon", "Delete individual memories or clear all at once"],
    tips: ["Memories are extracted automatically every few messages", "The more you share, the more personalized Omni becomes", "You have full control — delete anything you don't want remembered"],
  },
  {
    id: "voice", title: "Voice Interaction", icon: Volume2,
    what: "Speak to Omni and hear it respond with text-to-speech. Multiple voice options available.",
    when: "When you prefer speaking over typing, or want a hands-free experience.",
    steps: ["Tap the microphone button to speak your message", "Omni transcribes your speech and sends it", "Responses are read aloud if TTS is enabled", "Tap the speaker icon on any message to hear it", "Enable the \"Hey Omni\" wake word in Settings > Voice to start recording hands-free, no tapping needed"],
    tips: ["First tap of the mic shows a one-time 'Tap to speak' hint", "If the browser blocks the mic, allow it via the lock icon in the address bar", "Wisdom Council figures have unique voices assigned to them", "Long messages are automatically split into speakable chunks"],
  },
  {
    id: "mood", title: "Mood Tracking", icon: Heart,
    what: "Track your emotional state over time with quick mood check-ins. Omni auto-detects mood from conversations too.",
    when: "Daily check-ins, or whenever you want to log how you're feeling.",
    steps: ["Go to the Mood page from the sidebar", "Tap an emoji that matches your current mood", "Optionally add a note about what's happening", "View your mood history and trends over time"],
    tips: ["Omni also auto-detects mood from your chat messages", "Mood data helps Omni give more emotionally-aware responses"],
  },
  {
    id: "bookmarks", title: "Saved Items & Bookmarks", icon: Bookmark,
    what: "Save any message from any conversation. Bookmark insights, advice, or moments you want to revisit.",
    when: "Whenever Omni says something valuable you want to keep.",
    steps: ["Tap the bookmark icon below any AI or user message", "Access all bookmarks via the Bookmark icon in the header", "Jump to the original conversation from any bookmark", "Remove bookmarks you no longer need"],
    tips: ["Bookmarks work on both your messages and Omni's responses", "Use bookmarks to build a personal wisdom collection"],
  },
  {
    id: "backup", title: "Backup & Data", icon: HardDrive,
    what: "Full backup system — save all your conversations, memories, mood logs, and goals. Control your data completely.",
    when: "Periodically, or before any major changes. Set an auto-schedule and let Omni remind you.",
    steps: ["Go to Settings > Backup & Data", "Choose an auto-backup schedule (daily, weekly, monthly)", "Click 'Backup Now' for an instant manual backup", "Download any backup as a JSON file"],
    tips: ["Set weekly backups for peace of mind", "Omni alerts you if your backup is overdue"],
  },
  {
    id: "sharing", title: "Social Sharing", icon: Share2,
    what: "Share Omni's insights on Twitter/X, WhatsApp, Facebook, LinkedIn, Telegram, or by email. Choose full text or an AI-generated summary.",
    when: "When Omni gives you a response worth sharing with the world.",
    steps: ["Tap the share icon below any AI message", "Preview how the card will look", "Toggle between Full Text and AI Summary", "Click 'Share to Social Media' and pick a platform"],
    tips: ["AI Summary creates a tweet-worthy version automatically", "Wisdom Council shares include the figure's name as attribution"],
  },
  {
    id: "settings", title: "AI Personality Settings", icon: Palette,
    what: "Customize how Omni communicates — style, sensitivity, humor, and formality. Quick settings available from chat, full settings on the Settings page.",
    when: "When Omni's tone doesn't match your preference.",
    steps: ["Tap the gear icon in the chat header for Quick Settings", "Adjust Communication Style, Response Length, Sensitivity, Humor", "Changes apply instantly to your next message", "Visit Settings page for full control including backup and profile"],
    tips: ["'Adaptive' style mirrors your energy — great as default", "Increase emotional sensitivity if you want more empathetic responses"],
  },
  {
    id: "languages", title: "Multilingual Support", icon: Globe,
    what: "Omni supports 30+ languages. Switch languages and the entire interface adapts.",
    when: "If you prefer chatting in a language other than English.",
    steps: ["Go to Settings", "Find the Language section", "Select your preferred language", "The UI and Omni's responses adapt automatically"],
    tips: ["Omni responds in whatever language you write to it", "The Wisdom Council figures speak in your chosen language too"],
  },
  {
    id: "tiers", title: "Subscription Tiers", icon: Shield,
    what: "Free tier gives you daily message limits. Omni+, Family, Platinum, and Diamond tiers unlock more features, longer conversations, and exclusive modules.",
    when: "When you hit your daily limit or want access to exclusive features.",
    steps: ["Go to the Pricing page from the sidebar", "Compare tier features and limits", "Select a plan that fits your needs", "Add-on packages available for specific features"],
    tips: ["Free tier is generous for casual use", "Family plan supports multiple members with age-appropriate controls"],
  },
];

export default function GuidePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return GUIDE_SECTIONS;
    const q = search.toLowerCase();
    return GUIDE_SECTIONS.filter(s =>
      s.title.toLowerCase().includes(q) ||
      s.what.toLowerCase().includes(q) ||
      s.steps.some(st => st.toLowerCase().includes(q)) ||
      s.tips.some(t => t.toLowerCase().includes(q))
    );
  }, [search]);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2">Feature Guide</h1>
          <p className="text-sm text-muted-foreground">Everything you need to master Omni. Search or browse below.</p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="guide-search"
            type="text"
            placeholder="Search features..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-2xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
          />
          {search && (
            <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-3">
          {filtered.map(section => {
            const Icon = section.icon;
            const isExpanded = expandedId === section.id;
            return (
              <div
                key={section.id}
                data-testid={`guide-section-${section.id}`}
                className={cn(
                  "border rounded-2xl overflow-hidden transition-all",
                  isExpanded ? "border-primary/30 bg-card shadow-md" : "border-border/50 bg-card/50 hover:border-border"
                )}
              >
                <button
                  data-testid={`guide-toggle-${section.id}`}
                  onClick={() => setExpandedId(isExpanded ? null : section.id)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                >
                  <div className={cn(
                    "w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors",
                    isExpanded ? "bg-primary/10" : "bg-muted/50"
                  )}>
                    <Icon className={cn("w-4.5 h-4.5", isExpanded ? "text-primary" : "text-muted-foreground")} strokeWidth={1.5} />
                  </div>
                  <span className={cn("text-sm font-semibold flex-1", isExpanded && "text-primary")}>{section.title}</span>
                  <svg className={cn("w-4 h-4 text-muted-foreground transition-transform", isExpanded && "rotate-180")} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-5 pt-0 space-y-4 animate-in fade-in slide-in-from-top-1 duration-200">
                    {/* What it is */}
                    <div>
                      <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1">What it is</p>
                      <p className="text-sm text-foreground/80 leading-relaxed">{section.what}</p>
                    </div>
                    {/* When to use */}
                    <div>
                      <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1">When to use</p>
                      <p className="text-sm text-foreground/80 leading-relaxed">{section.when}</p>
                    </div>
                    {/* Step by step */}
                    <div>
                      <p className="text-[10px] font-bold text-primary uppercase tracking-wider mb-1.5">Step by step</p>
                      <ol className="space-y-1.5">
                        {section.steps.map((step, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/80">
                            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                    {/* Pro tips */}
                    <div>
                      <p className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1.5">Pro tips</p>
                      <ul className="space-y-1">
                        {section.tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-foreground/70">
                            <span className="text-amber-500 mt-0.5">&#9679;</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Search className="w-8 h-8 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No features match "{search}"</p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
